import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar, Newspaper, Plus, Edit2, Trash2, Users, Store, ShoppingBag, 
  Package, BarChart3, CheckCircle, XCircle, AlertCircle, ArrowLeft,
  Clock, MapPin, DollarSign, Image as ImageIcon, Star, BadgeCheck, Mail, ShieldQuestion, Globe, Inbox, Trophy, UserPlus
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdminVerificationsTab from "@/components/AdminVerificationsTab";
import AdminEmailTab from "@/components/AdminEmailTab";
import AdminClaimsTab from "@/components/AdminClaimsTab";
import AdminBusinessesTab from "@/components/AdminBusinessesTab";
import AdminBackupCard from "@/components/AdminBackupCard";
import AdminNormalizeImagesCard from "@/components/AdminNormalizeImagesCard";
import AdminNewsQueueTab from "@/components/AdminNewsQueueTab";
import AdminEventsQueueTab from "@/components/AdminEventsQueueTab";
import AdminPlacesImportTab from "@/components/AdminPlacesImportTab";
import AdminTrafficTab from "@/components/AdminTrafficTab";
import AdminSpotlightTab from "@/components/AdminSpotlightTab";
import AdminBlinterestTab from "@/components/AdminBlinterestTab";
import AdminAcademyCard from "@/components/AdminAcademyCard";
import AdminBusinessSubmissionsTab from "@/components/AdminBusinessSubmissionsTab";
import AdminUsersTab from "@/components/AdminUsersTab";
import AdminFounderTab from "@/components/AdminFounderTab";
import AdminBusinessManagementTab from "@/components/AdminBusinessManagementTab";
import AdminHeaderBusinessSearch from "@/components/AdminHeaderBusinessSearch";
import AdminSidebarNav from "@/components/AdminSidebarNav";
import AdminDashboardTab from "@/components/AdminDashboardTab";
import AdminOrdersTab from "@/components/AdminOrdersTab";
import AdminMobileMenu from "@/components/AdminMobileMenu";
import { toast } from "sonner";

const EVENT_CATEGORIES = ["Networking", "Conference", "Tech", "Workshop", "Education", "Market"];
const ARTICLE_CATEGORIES = ["Featured", "Culture", "Technology", "News", "Business"];

// Event Form Component
const EventForm = ({ event, onSubmit, onClose }) => {
  const [form, setForm] = useState(event || {
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    venue: "",
    category: "Networking",
    image: "",
    price: "Free",
    max_attendees: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(form);
      onClose();
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error saving event:", error);
      alert("Failed to save event");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Event title"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Event description"
          rows={3}
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
          <Input
            value={form.time}
            onChange={(e) => setForm({ ...form, time: e.target.value })}
            placeholder="e.g. 6:00 PM - 9:00 PM"
            required
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
          <Input
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="City, State"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
          <Input
            value={form.venue}
            onChange={(e) => setForm({ ...form, venue: e.target.value })}
            placeholder="Venue name"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
          <Input
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            placeholder="Free or $XX"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
        <Input
          value={form.image}
          onChange={(e) => setForm({ ...form, image: e.target.value })}
          placeholder="https://..."
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={loading} className="bg-tbc-navy hover:bg-tbc-navy/90">
          {loading ? "Saving..." : (event ? "Update Event" : "Create Event")}
        </Button>
      </div>
    </form>
  );
};

// Article Form Component
const ArticleForm = ({ article, onSubmit, onClose }) => {
  const [form, setForm] = useState(article || {
    title: "",
    excerpt: "",
    content: "",
    author: "TBC Editorial",
    category: "Food & Beverage",
    image: "",
    featured: false
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState(ARTICLE_CATEGORIES);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/article-categories`);
        if (!cancelled && Array.isArray(r.data?.categories)) {
          setCategories(r.data.categories);
        }
      } catch {
        // silently keep the fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(form);
      onClose();
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Error saving article:", error);
      alert("Failed to save article");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Article title"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt *</label>
        <Textarea
          value={form.excerpt}
          onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          placeholder="Brief summary shown in listings"
          rows={2}
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
        <Textarea
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Full article content..."
          rows={8}
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Author *</label>
          <Input
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            placeholder="Author name"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
        <Input
          value={form.image}
          onChange={(e) => setForm({ ...form, image: e.target.value })}
          placeholder="https://..."
        />
      </div>
      
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="featured"
          checked={form.featured}
          onChange={(e) => setForm({ ...form, featured: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-tbc-navy focus:ring-tbc-navy"
        />
        <label htmlFor="featured" className="text-sm text-gray-700">Featured article (shown prominently)</label>
      </div>
      
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={loading} className="bg-tbc-navy hover:bg-tbc-navy/90">
          {loading ? "Saving..." : (article ? "Update Article" : "Create Article")}
        </Button>
      </div>
    </form>
  );
};

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "dashboard";
  const initialFocus = searchParams.get("focus") || null;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [focus, setFocus] = useState(initialFocus);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Events state
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showArticleForm, setShowArticleForm] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch stats:", error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data.events || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch events:", error);
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data.articles || []);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Failed to fetch articles:", error);
    }
  }, []);

  const checkAdmin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, {
        withCredentials: true
      });
      setIsAdmin(response.data.is_admin);
      if (response.data.is_admin) {
        await Promise.all([fetchStats(), fetchEvents(), fetchArticles()]);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Admin check failed:", error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [fetchStats, fetchEvents, fetchArticles]);

  useEffect(() => {
    // Wait for auth context to finish loading before deciding what to do
    if (authLoading) return;
    if (!user) {
      navigate("/");
      return;
    }
    checkAdmin();
  }, [user, authLoading, checkAdmin, navigate]);

  // Event handlers
  const handleCreateEvent = async (eventData) => {
    await axios.post(`${API}/events`, eventData, {
      withCredentials: true
    });
    fetchEvents();
  };

  const handleUpdateEvent = async (eventData) => {
    await axios.put(`${API}/events/${editingEvent.event_id}`, eventData, {
      withCredentials: true
    });
    setEditingEvent(null);
    fetchEvents();
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm("Are you sure you want to delete this event?")) return;
    await axios.delete(`${API}/events/${eventId}`, {
      withCredentials: true
    });
    fetchEvents();
  };

  // Article handlers
  const handleCreateArticle = async (articleData) => {
    await axios.post(`${API}/articles`, articleData, {
      withCredentials: true
    });
    fetchArticles();
  };

  const handleUpdateArticle = async (articleData) => {
    await axios.put(`${API}/articles/${editingArticle.article_id}`, articleData, {
      withCredentials: true
    });
    setEditingArticle(null);
    fetchArticles();
  };

  const handleDeleteArticle = async (articleId) => {
    if (!confirm("Are you sure you want to delete this article?")) return;
    await axios.delete(`${API}/articles/${articleId}`, {
      withCredentials: true
    });
    fetchArticles();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tbc-navy"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <AlertCircle className="w-16 h-16 text-tbc-coral mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-tbc-navy mb-2">Access Denied</h1>
          <p className="text-gray-600 font-body mb-6">
            You don&apos;t have permission to access the admin panel. Contact the site administrator if you believe this is an error.
          </p>
          <Button onClick={() => navigate("/")} className="bg-tbc-navy hover:bg-tbc-navy/90">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <section className="bg-tbc-navy py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl text-white">Admin Panel</h1>
          <p className="text-gray-300 font-body mb-4">Manage events, news, and content</p>
          <AdminHeaderBusinessSearch />
        </div>
      </section>

      {/* Stats */}
      <section className="py-6 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-tbc-cream rounded-lg p-4 text-center">
              <Store className="w-6 h-6 text-tbc-gold mx-auto mb-2" />
              <p className="text-2xl font-heading text-tbc-navy">{stats.businesses || 0}</p>
              <p className="text-xs text-gray-500">Businesses</p>
            </div>
            <div className="bg-tbc-cream rounded-lg p-4 text-center">
              <Package className="w-6 h-6 text-tbc-gold mx-auto mb-2" />
              <p className="text-2xl font-heading text-tbc-navy">{stats.products || 0}</p>
              <p className="text-xs text-gray-500">Products</p>
            </div>
            <div className="bg-tbc-cream rounded-lg p-4 text-center">
              <ShoppingBag className="w-6 h-6 text-tbc-gold mx-auto mb-2" />
              <p className="text-2xl font-heading text-tbc-navy">{stats.orders || 0}</p>
              <p className="text-xs text-gray-500">Orders</p>
            </div>
            <div
              className="bg-tbc-cream rounded-lg p-4 text-center cursor-pointer hover:bg-tbc-gold/10 transition-colors"
              onClick={() => setActiveTab("users")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActiveTab("users")}
              data-testid="admin-stat-users-clickable"
            >
              <Users className="w-6 h-6 text-tbc-gold mx-auto mb-2" />
              <p className="text-2xl font-heading text-tbc-navy">{stats.users || 0}</p>
              <p className="text-xs text-gray-500">Users</p>
            </div>
            <div className="bg-tbc-cream rounded-lg p-4 text-center">
              <Calendar className="w-6 h-6 text-tbc-gold mx-auto mb-2" />
              <p className="text-2xl font-heading text-tbc-navy">{stats.events || 0}</p>
              <p className="text-xs text-gray-500">Events</p>
            </div>
            <div className="bg-tbc-cream rounded-lg p-4 text-center">
              <Newspaper className="w-6 h-6 text-tbc-gold mx-auto mb-2" />
              <p className="text-2xl font-heading text-tbc-navy">{stats.articles || 0}</p>
              <p className="text-xs text-gray-500">Articles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Management */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-6">
          {/* Permanent left sidebar (desktop only) */}
          <AdminSidebarNav
            activeTab={activeTab}
            focus={focus}
            onSelect={(tab, nextFocus) => {
              setActiveTab(tab);
              setFocus(nextFocus || null);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", tab);
                if (nextFocus) next.set("focus", nextFocus);
                else next.delete("focus");
                return next;
              });
            }}
          />

          <div className="flex-1 min-w-0">
          {/* Mobile: single Admin Menu drawer replaces the overflowing tab list */}
          <AdminMobileMenu
            activeTab={activeTab}
            focus={focus}
            onSelect={(tab, nextFocus) => {
              setActiveTab(tab);
              setFocus(nextFocus || null);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", tab);
                if (nextFocus) next.set("focus", nextFocus);
                else next.delete("focus");
                return next;
              });
            }}
          />

          <Tabs
            value={activeTab}
            onValueChange={(val) => {
              setActiveTab(val);
              setFocus(null);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", val);
                next.delete("focus");
                return next;
              });
            }}
            className="w-full"
          >
            {/* Horizontal tab list — completely hidden. The mobile drawer
                (above) and the desktop sidebar (beside) drive navigation.
                Kept in the DOM so the underlying Tabs primitive still
                receives its trigger metadata. */}
            <TabsList className="hidden">
              <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="admin-tab-dashboard">
                <BarChart3 className="w-4 h-4" /> Dashboard
              </TabsTrigger>
              <TabsTrigger value="verifications" className="flex items-center gap-2" data-testid="admin-tab-verifications">
                <BadgeCheck className="w-4 h-4" /> Verifications
              </TabsTrigger>
              <TabsTrigger value="biz-mgmt" className="flex items-center gap-2" data-testid="admin-tab-biz-mgmt">
                <Store className="w-4 h-4" /> Business Management
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="admin-tab-orders">
                <ShoppingBag className="w-4 h-4" /> Orders
              </TabsTrigger>
              <TabsTrigger value="businesses" className="flex items-center gap-2" data-testid="admin-tab-businesses">
                <Store className="w-4 h-4" /> Businesses (legacy)
              </TabsTrigger>
              <TabsTrigger value="spotlight" className="flex items-center gap-2" data-testid="admin-tab-spotlight">
                <Trophy className="w-4 h-4" /> Spotlight
              </TabsTrigger>
              <TabsTrigger value="founding" className="flex items-center gap-2" data-testid="admin-tab-founding">
                <Trophy className="w-4 h-4" /> Founding
              </TabsTrigger>
              <TabsTrigger value="blinterest" className="flex items-center gap-2" data-testid="admin-tab-blinterest">
                <Trophy className="w-4 h-4" /> Blinterest
              </TabsTrigger>
              <TabsTrigger value="events" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Events
              </TabsTrigger>
              <TabsTrigger value="articles" className="flex items-center gap-2">
                <Newspaper className="w-4 h-4" /> News/Articles
              </TabsTrigger>
              <TabsTrigger value="news-queue" className="flex items-center gap-2" data-testid="admin-tab-news-queue">
                <Newspaper className="w-4 h-4" /> News Queue
              </TabsTrigger>
              <TabsTrigger value="events-queue" className="flex items-center gap-2" data-testid="admin-tab-events-queue">
                <Inbox className="w-4 h-4" /> Events Queue
              </TabsTrigger>
              <TabsTrigger value="submissions" className="flex items-center gap-2" data-testid="admin-tab-submissions">
                <UserPlus className="w-4 h-4" /> Submissions
              </TabsTrigger>
              <TabsTrigger value="places" className="flex items-center gap-2" data-testid="admin-tab-places">
                <Globe className="w-4 h-4" /> Places Import
              </TabsTrigger>
              <TabsTrigger value="traffic" className="flex items-center gap-2" data-testid="admin-tab-traffic">
                <BarChart3 className="w-4 h-4" /> Traffic
              </TabsTrigger>
              <TabsTrigger value="email" className="flex items-center gap-2" data-testid="admin-tab-email">
                <Mail className="w-4 h-4" /> Email
              </TabsTrigger>
              <TabsTrigger value="claims" className="flex items-center gap-2" data-testid="admin-tab-claims">
                <ShieldQuestion className="w-4 h-4" /> Claims
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab — landing view with 4 overview KPI cards */}
            <TabsContent value="dashboard">
              <AdminDashboardTab />
            </TabsContent>

            {/* Orders Tab — platform-wide orders feed */}
            <TabsContent value="orders">
              <AdminOrdersTab />
            </TabsContent>

            {/* Verifications Tab */}
            <TabsContent value="verifications">
              <AdminVerificationsTab />
            </TabsContent>

            {/* Business Management Tab — Super Admin global console */}
            <TabsContent value="biz-mgmt">
              <AdminBusinessManagementTab />
            </TabsContent>

            {/* Businesses Tab (legacy — Google Places imports + bulk tools) */}
            <TabsContent value="businesses">
              <div className="space-y-6">
                <AdminBackupCard />
                <AdminNormalizeImagesCard />
                <AdminBusinessesTab />
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <AdminUsersTab />
            </TabsContent>

            {/* Spotlight Tab — Businesses of the Week roster manager */}
            <TabsContent value="spotlight">
              <AdminSpotlightTab />
            </TabsContent>

            {/* Founding Tab — Founder program control center */}
            <TabsContent value="founding">
              <AdminFounderTab />
            </TabsContent>

            {/* Blinterest Tab — moderation queue + ingester triggers */}
            <TabsContent value="blinterest">
              <AdminBlinterestTab />
              <div className="mt-6">
                <AdminAcademyCard />
              </div>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email">
              <AdminEmailTab />
            </TabsContent>

            {/* Claims Tab */}
            <TabsContent value="claims">
              <AdminClaimsTab />
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="font-heading text-xl text-tbc-navy">Manage Events</h2>
                  <Dialog open={showEventForm} onOpenChange={setShowEventForm}>
                    <DialogTrigger asChild>
                      <Button className="bg-tbc-coral hover:bg-tbc-coral/90">
                        <Plus className="w-4 h-4 mr-2" /> Add Event
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create New Event</DialogTitle>
                      </DialogHeader>
                      <EventForm 
                        onSubmit={handleCreateEvent} 
                        onClose={() => setShowEventForm(false)} 
                      />
                    </DialogContent>
                  </Dialog>
                </div>

                {events.length > 0 ? (
                  <div className="divide-y">
                    {events.map((event) => (
                      <div key={event.event_id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                        {event.image && (
                          <img 
                            src={event.image} 
                            alt={event.title}
                            className="w-20 h-14 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-tbc-navy truncate">{event.title}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {formatDate(event.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {event.location}
                            </span>
                            <span className="px-2 py-0.5 bg-tbc-coral/10 text-tbc-coral rounded text-xs">
                              {event.category}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Dialog open={editingEvent?.event_id === event.event_id} onOpenChange={(open) => !open && setEditingEvent(null)}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setEditingEvent(event)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Edit Event</DialogTitle>
                              </DialogHeader>
                              <EventForm 
                                event={editingEvent}
                                onSubmit={handleUpdateEvent} 
                                onClose={() => setEditingEvent(null)} 
                              />
                            </DialogContent>
                          </Dialog>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteEvent(event.event_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No events yet. Create your first event!</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* News Queue Tab (RSS-ingested articles awaiting admin review) */}
            <TabsContent value="news-queue">
              <AdminNewsQueueTab />
            </TabsContent>

            {/* Events Queue Tab (member-submitted events awaiting review) */}
            <TabsContent value="events-queue">
              <AdminEventsQueueTab />
            </TabsContent>

            {/* Submissions Tab — community-nominated businesses awaiting review */}
            <TabsContent value="submissions">
              <AdminBusinessSubmissionsTab />
            </TabsContent>

            {/* Places Import Tab (Google Places recruitment) */}
            <TabsContent value="places">
              <AdminPlacesImportTab />
            </TabsContent>

            {/* Site-wide Traffic Dashboard */}
            <TabsContent value="traffic">
              <AdminTrafficTab />
            </TabsContent>


            {/* Articles Tab */}
            <TabsContent value="articles">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center">
                  <h2 className="font-heading text-xl text-tbc-navy">Manage Articles</h2>
                  <Dialog open={showArticleForm} onOpenChange={setShowArticleForm}>
                    <DialogTrigger asChild>
                      <Button className="bg-tbc-coral hover:bg-tbc-coral/90">
                        <Plus className="w-4 h-4 mr-2" /> Add Article
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create New Article</DialogTitle>
                      </DialogHeader>
                      <ArticleForm 
                        onSubmit={handleCreateArticle} 
                        onClose={() => setShowArticleForm(false)} 
                      />
                    </DialogContent>
                  </Dialog>
                </div>

                {articles.length > 0 ? (
                  <div className="divide-y">
                    {articles.map((article) => (
                      <div key={article.article_id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                        {article.image && (
                          <img 
                            src={article.image} 
                            alt={article.title}
                            className="w-20 h-14 object-cover rounded"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-tbc-navy truncate">{article.title}</h3>
                            {article.featured && (
                              <Star className="w-4 h-4 text-tbc-gold fill-tbc-gold" />
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>{article.author}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {article.read_time}
                            </span>
                            <span className="px-2 py-0.5 bg-tbc-gold/10 text-tbc-navy rounded text-xs">
                              {article.category}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Dialog open={editingArticle?.article_id === article.article_id} onOpenChange={(open) => !open && setEditingArticle(null)}>
                            <DialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setEditingArticle(article)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Edit Article</DialogTitle>
                              </DialogHeader>
                              <ArticleForm 
                                article={editingArticle}
                                onSubmit={handleUpdateArticle} 
                                onClose={() => setEditingArticle(null)} 
                              />
                            </DialogContent>
                          </Dialog>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteArticle(article.article_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No articles yet. Create your first article!</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-8 bg-tbc-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-6">
            <h3 className="font-heading text-lg text-tbc-navy mb-4">Quick Tips</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Events</p>
                  <p>Add community events, workshops, and networking opportunities. They&apos;ll appear on the Events page.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Articles</p>
                  <p>Write news stories, business tips, and spotlights. Mark articles as &quot;Featured&quot; for prominent display.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Images</p>
                  <p>Use direct image URLs from Unsplash, Pexels, or your own hosted images.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium">Need Help?</p>
                  <p>You can also send content directly to the development team for assistance.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
